import type { Map as ImmutableMap } from 'immutable';
import { shallowEqual } from 'react-redux';
import { createSelector, createSelectorCreator, lruMemoize, weakMapMemoize } from 'reselect';

import type RootState from 'app-components/utils/redux-store/rootState';

import { registerMemoizedCache } from './selectorCacheRegistry';

/** lruMemoize whose entries a store reset clears (see selectorCacheRegistry). */
export const resettableLruMemoize: typeof lruMemoize = (func, options) => {
  const memoized = lruMemoize(func, options);
  registerMemoizedCache(memoized);
  return memoized;
};

// use if you only need your selector in one component
export const createAppSelector = createSelector.withTypes<RootState>();

/** Compare entry references without changing the collection's observable iteration order. */
export function immutableEntriesShallowEqual<K, V>(
  previous: ImmutableMap<K, V> | undefined,
  next: ImmutableMap<K, V> | undefined,
): boolean {
  if (previous === next) return true;
  if (!previous || !next || previous.size !== next.size) return false;
  const nextEntries = next.entries();
  for (const [key, value] of previous) {
    const entry = nextEntries.next();
    if (entry.done || key !== entry.value[0] || value !== entry.value[1]) return false;
  }
  return true;
}

// Use if you need your selector in multiple components with different args
export const createWeakMapSelector = createSelectorCreator({
  memoize: weakMapMemoize,
  argsMemoize: weakMapMemoize,
});

// Use if you need your selector in multiple components with heavy computation and different arguments (eg reports)
export const createSelectorShallowEqual = createSelectorCreator({
  memoize: resettableLruMemoize,
  memoizeOptions: {
    equalityCheck: shallowEqual,
    resultEqualityCheck: shallowEqual,
    maxSize: 10,
  },
  argsMemoize: resettableLruMemoize,
  argsMemoizeOptions: {
    equalityCheck: shallowEqual,
    resultEqualityCheck: shallowEqual,
    maxSize: 10,
  },
});

// https://reselect.js.org/api/lrumemoize/
//
// NOTE: You can always add your own custom equality checks. Here is an example:
// const selectTodoIds = createAppSelector(
//   [(state: RootState) => state.todos],
//   todos => todos.map(todo => todo.id),
//   {
//     memoize: lruMemoize,
//     memoizeOptions: {
//       equalityCheck: (a, b) => a.firstProperty === b.firstProperty,
//       resultEqualityCheck: (a, b) => a.firstProperty === b.firstProperty,,
//       maxSize: 10
//     },
//     argsMemoize: lruMemoize, // this is for memoizing arguments, it is optional
//     argsMemoizeOptions: {
//       equalityCheck: (a, b) => a.firstProperty === b.firstProperty,,
//       resultEqualityCheck: (a, b) => a.firstProperty === b.firstProperty,,
//       maxSize: 10
//     }
//   }
// )
